import React from "react";

import { Moment } from "moment";

import { useDateFormatContext } from "commons/context/dateFormat";

import moment from "commons/utils/moment";

export type HookType = {
    formatDate: (unixWithMilliSeconds?: number | null) => string;
    formatDateTime: (unixWithMilliSeconds?: number | null) => string;
    formatDateTimeSplitted: (unixWithMilliSeconds?: number | null) => string;
    formatDatetimeWithoutSeconds: (unixWithMilliSeconds?: number | null) => string;
    formatLocalDate: (dateToTransform?: string | null) => string;
    formatMonth: (unixWithMilliSeconds?: number | null) => string;
    formatNumberMonth: (unixWithMilliSeconds?: number | null) => string;
    formatTime: (unixWithMilliSeconds?: number | null) => string;
    formatTimeWithSeconds: (unixWithMilliSeconds?: number | null) => string;
    getPlaceholderDate: () => string;
    getPlaceholderYearMonth: () => string;
    getMaskDate: () => string;
    getMaskDateTime: () => string;
    getMaskTime: () => string;
    getMaskYearMonth: () => string;
    getFormatDate: () => string;
    getFormatNumberMonth: () => string;
    getFormatDateTimeWoSeconds: () => string;
    getFormatTimeWoSeconds: () => string;
    createMomentFromNumberMonth: (input: string, strict?: boolean) => Moment;
};

function useDateFormat(): HookType {
    const {
        momentDate,
        momentDateTime,
        momentDateTimeWoSeconds,
        momentTime,
        momentTimeWoSeconds,
        momentMonth,
        momentNumberMonth,
        placeholderDate,
        placeholderYearMonth,
        maskDate,
        maskDateTime,
        maskTime,
        maskYearMonth,
        separator,
    } = useDateFormatContext();

    const formatDate = React.useCallback(
        (unixWithMilliSeconds?: number | null): string => {
            return unixWithMilliSeconds ? moment(unixWithMilliSeconds).format(momentDate) : "";
        },
        [momentDate]
    );

    const formatDateTime = React.useCallback(
        (unixWithMilliSeconds?: number | null): string => {
            return unixWithMilliSeconds ? moment(unixWithMilliSeconds).format(momentDateTime) : "";
        },
        [momentDateTime]
    );

    const formatDateTimeSplitted = React.useCallback(
        (unixWithMilliSeconds?: number | null): string => {
            return unixWithMilliSeconds
                ? `${moment(unixWithMilliSeconds).format(momentDate)} ${separator} ${moment(
                      unixWithMilliSeconds
                  ).format(momentTime)}`
                : "";
        },
        [momentDate, momentTime, separator]
    );

    const formatDatetimeWithoutSeconds = React.useCallback(
        (unixWithMilliSeconds?: number | null): string => {
            return unixWithMilliSeconds
                ? moment(unixWithMilliSeconds).format(momentDateTimeWoSeconds)
                : "";
        },
        [momentDateTimeWoSeconds]
    );

    const formatLocalDate = React.useCallback(
        (dateToTransform?: string | null): string => {
            return dateToTransform
                ? formatDate(parseInt(moment(dateToTransform, "YYYY-MM-DD").format("x")))
                : "";
        },
        [formatDate]
    );

    const formatMonth = React.useCallback(
        (unixWithMilliSeconds?: number | null): string => {
            return unixWithMilliSeconds ? moment(unixWithMilliSeconds).format(momentMonth) : "";
        },
        [momentMonth]
    );

    const formatNumberMonth = React.useCallback(
        (unixWithMilliSeconds?: number | null): string => {
            return unixWithMilliSeconds
                ? moment(unixWithMilliSeconds).format(momentNumberMonth)
                : "";
        },
        [momentNumberMonth]
    );

    const formatTime = React.useCallback(
        (unixWithMilliSeconds?: number | null): string => {
            return unixWithMilliSeconds
                ? moment(unixWithMilliSeconds).format(momentTimeWoSeconds)
                : "";
        },
        [momentTimeWoSeconds]
    );

    const formatTimeWithSeconds = React.useCallback(
        (unixWithMilliSeconds?: number | null): string => {
            return unixWithMilliSeconds ? moment(unixWithMilliSeconds).format(momentTime) : "";
        },
        [momentTime]
    );

    const getPlaceholderDate = React.useCallback((): string => {
        return placeholderDate;
    }, [placeholderDate]);

    const getPlaceholderYearMonth = React.useCallback((): string => {
        return placeholderYearMonth;
    }, [placeholderYearMonth]);

    const getMaskDate = React.useCallback((): string => {
        return maskDate;
    }, [maskDate]);

    const getMaskDateTime = React.useCallback((): string => {
        return maskDateTime;
    }, [maskDateTime]);

    const getMaskTime = React.useCallback((): string => {
        return maskTime;
    }, [maskTime]);

    const getMaskYearMonth = React.useCallback((): string => {
        return maskYearMonth;
    }, [maskYearMonth]);

    const getFormatDate = React.useCallback((): string => {
        return momentDate;
    }, [momentDate]);

    const getFormatNumberMonth = React.useCallback((): string => {
        return momentNumberMonth;
    }, [momentNumberMonth]);

    const getFormatDateTimeWoSeconds = React.useCallback((): string => {
        return momentDateTimeWoSeconds;
    }, [momentDateTimeWoSeconds]);

    const getFormatTimeWoSeconds = React.useCallback((): string => {
        return momentTimeWoSeconds;
    }, [momentTimeWoSeconds]);

    const createMomentFromNumberMonth = React.useCallback(
        (input: string, strict?: boolean): Moment => {
            return moment(input, momentNumberMonth, strict);
        },
        [momentNumberMonth]
    );

    return React.useMemo(
        (): HookType => ({
            formatDate,
            formatDateTime,
            formatDateTimeSplitted,
            formatDatetimeWithoutSeconds,
            formatLocalDate,
            formatMonth,
            formatNumberMonth,
            formatTime,
            formatTimeWithSeconds,
            getPlaceholderDate,
            getPlaceholderYearMonth,
            getMaskDate,
            getMaskDateTime,
            getMaskTime,
            getMaskYearMonth,
            getFormatDate,
            getFormatNumberMonth,
            getFormatDateTimeWoSeconds,
            getFormatTimeWoSeconds,
            createMomentFromNumberMonth,
        }),
        [
            formatDate,
            formatDateTime,
            formatDateTimeSplitted,
            formatDatetimeWithoutSeconds,
            formatLocalDate,
            formatMonth,
            formatNumberMonth,
            formatTime,
            formatTimeWithSeconds,
            getPlaceholderDate,
            getPlaceholderYearMonth,
            getMaskDate,
            getMaskDateTime,
            getMaskTime,
            getMaskYearMonth,
            getFormatDate,
            getFormatNumberMonth,
            getFormatDateTimeWoSeconds,
            getFormatTimeWoSeconds,
            createMomentFromNumberMonth,
        ]
    );
}

export default useDateFormat;
